// `localStorage.setItem(k, undefined)` stringifies its argument and writes the
// literal "undefined" / "null". Treat those as missing so a previous bad write
// doesn't get forwarded into Redux/apiClient and trigger a 401 loop.
export const sanitizeToken = (
  token: string | null | undefined,
): string | null => {
  if (!token || token === 'undefined' || token === 'null') return null;
  return token;
};

// Admin "Debug Mode": the session is an impersonated learner, on the admin's
// own browser.
export const isAdminMode = (): boolean => {
  try {
    return JSON.parse(localStorage.getItem('isAdminMode') || 'false');
  } catch {
    return false;
  }
};

const decodeJwtClaims = (token: string): Record<string, unknown> | null => {
  const segment = token.split('.')[1];
  if (!segment) return null;
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  try {
    return JSON.parse(
      atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')),
    );
  } catch {
    return null;
  }
};

/** A v1 "forever token" from the retired `/auth` flow carries no `exp`. The
 * backend rejects those outright and their holders never got a refresh cookie,
 * so booting the app with one guarantees a 401 and an eject to the landing
 * page — a flash of the signed-in UI, then the user is gone.
 *
 * Deliberately does *not* reject a well-formed token whose `exp` has passed:
 * that one still refreshes silently off the cookie, which is the common case.
 * A payload we can't parse is left alone too — only a token we can read and
 * that provably has no `exp` is treated as unusable. */
const isV1ForeverToken = (token: string): boolean => {
  const claims = decodeJwtClaims(token);
  return claims !== null && typeof claims.exp !== 'number';
};

const TOKEN_KEYS = ['loginToken', 'secondaryToken'] as const;

/** Drop every stored v1 forever-token, before anything can read one.
 *
 * Call this once at boot. The access token is pulled straight out of
 * localStorage in a dozen places — both public layouts, `autoLogin`, the socket
 * handshake, the auth slice, the raw-fetch download helpers — and only the
 * `/home` entry goes through `getStoredToken`. Purging at that single read left
 * every bookmark, deep link and email link still booting on a token the backend
 * rejects by design, so they kept getting the same flash-then-eject. */
export const purgeUnusableTokens = (): void => {
  try {
    TOKEN_KEYS.forEach((key) => {
      const token = sanitizeToken(localStorage.getItem(key));
      if (token && isV1ForeverToken(token)) localStorage.removeItem(key);
    });
  } catch {
    // Storage unavailable (private mode, site data blocked). There is nothing
    // to purge in that case, and this runs before the first render — throwing
    // here would take the whole app down.
  }
};

const getStoredToken = (): string | null => {
  const key = isAdminMode() ? 'secondaryToken' : 'loginToken';
  const token = sanitizeToken(localStorage.getItem(key));
  if (token && isV1ForeverToken(token)) {
    // Defence in depth: `purgeUnusableTokens` already ran at boot, but a token
    // can also arrive after it — `pages/authentication` writes one straight
    // from an `?access_token=` handoff.
    localStorage.removeItem(key);
    return null;
  }
  return token;
};

export default getStoredToken;
